import { useEffect, useState } from "react";
import { getScreeningCounts, type ScreeningCounts } from "@/src/services/dashboard_counts_service";
import {
  getMonitoringKpi,
  getNewMatchesPreview,
  getMonitoringTrend,
  type MonitoringKpi,
  type MonitoringTrend,
} from "@/src/services/monitoring_service";

export type ScreeningWidgetData = { loading: boolean; counts: ScreeningCounts | null };

export function useScreeningCounts(
  live: boolean,
  infoResolved: boolean,
): ScreeningWidgetData | null {
  const [counts, setCounts] = useState<ScreeningCounts | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!live) {
      setCounts(null);
      setLoading(false);
      return;
    }
    if (!infoResolved) {
      setLoading(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getScreeningCounts()
      .then((res) => {
        if (cancelled) return;
        setCounts(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCounts(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [live, infoResolved]);

  return live ? { loading, counts } : null;
}

export type MonitoringWidgetData = {
  loading: boolean;
  /** Account-wide monitored-names totals (GET monitoring/statistics/). */
  kpi: MonitoringKpi | null;
  /** Entries with at least one NEW (unreviewed) match — the open alerts. */
  openAlertsCount: number;
};

export function useMonitoringPreview(
  live: boolean,
  infoResolved: boolean,
): MonitoringWidgetData | null {
  const [kpi, setKpi] = useState<MonitoringKpi | null>(null);
  const [openAlertsCount, setOpenAlertsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!live) {
      setKpi(null);
      setOpenAlertsCount(0);
      setLoading(false);
      return;
    }
    if (!infoResolved) {
      setLoading(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getMonitoringKpi(), getNewMatchesPreview()]).then(([kpiRes, alertsRes]) => {
      if (cancelled) return;
      setKpi(kpiRes.ok ? kpiRes.value : { total: 0, matched: 0, matchRate: 0, lastAlertAt: null });
      setOpenAlertsCount(alertsRes.ok ? alertsRes.value.count : 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [live, infoResolved]);

  return live ? { loading, kpi, openAlertsCount } : null;
}

export type MonitoringTrendData = { loading: boolean; trend: MonitoringTrend | null };

export function useMonitoringTrend(
  live: boolean,
  infoResolved: boolean,
): MonitoringTrendData | null {
  const [trend, setTrend] = useState<MonitoringTrend | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!live) {
      setTrend(null);
      setLoading(false);
      return;
    }
    if (!infoResolved) {
      setLoading(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMonitoringTrend().then((res) => {
      if (cancelled) return;
      setTrend(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [live, infoResolved]);

  return live ? { loading, trend } : null;
}
